import { AfterViewInit, Component, ElementRef, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MapsAPILoader } from '@agm/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  OptimizationService,
  RouteOptimizationEndpoint,
  RouteOptimizationEndpoints,
  RouteOptimizationPreview
} from '../../../../@core/services/optimization.service';
import { Subscription } from 'rxjs';

type RouteEndpointChoice = 'location' | 'depot' | 'lastStop' | 'custom';
export interface RouteEndpointsDialogData {
  routeId: number;
  routeDate?: Date;
  stopCount?: number;
  editOnly?: boolean;
}

@Component({
  selector: 'ngx-route-endpoints-dialog',
  templateUrl: './route-endpoints-dialog.component.html',
  styleUrls: ['./route-endpoints-dialog.component.scss']
})
export class RouteEndpointsDialogComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() data: RouteEndpointsDialogData;
  @ViewChild('startSearch', { static: true }) startSearch: ElementRef<HTMLInputElement>;
  @ViewChild('endSearch', { static: true }) endSearch: ElementRef<HTMLInputElement>;

  start: RouteOptimizationEndpoint | null = null;
  end: RouteOptimizationEndpoint | null = null;
  startText = '';
  endText = '';
  depotAddress = '';
  depotLatitude: number | null = null;
  depotLongitude: number | null = null;
  loading = true;
  loadFailed = false;
  saving = false;
  error = '';
  startChoice: RouteEndpointChoice = 'depot';
  endChoice: RouteEndpointChoice = 'depot';
  preview: RouteOptimizationPreview | null = null;
  endAtLastStopSupported = false;
  stopPage = 0;
  stopPageSize = 25;
  private loadSubscription: Subscription;
  private loadRequest = 0;
  private initialized = false;
  private destroyed = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: RouteEndpointsDialogData,
    private dialogRef: MatDialogRef<RouteEndpointsDialogComponent>,
    private optimizationService: OptimizationService,
    private mapsApiLoader: MapsAPILoader,
    private zone: NgZone,
    private translate: TranslateService
  ) { this.data = data; }

  ngOnInit(): void {
    this.initialized = true;
    this.loadEndpoints();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && changes.data) {
      this.loadEndpoints();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.loadRequest++;
    if (this.loadSubscription) { this.loadSubscription.unsubscribe(); }
  }

  loadEndpoints(): void {
    const request = ++this.loadRequest;
    const routeId = this.data.routeId;
    if (this.loadSubscription) { this.loadSubscription.unsubscribe(); }
    this.loading = true;
    this.saving = false;
    this.loadFailed = false;
    this.error = '';
    this.preview = null;
    this.endAtLastStopSupported = false;
    this.stopPage = 0;
    this.start = this.end = null;
    this.startText = this.endText = this.depotAddress = '';
    this.depotLatitude = this.depotLongitude = null;
    this.startChoice = this.endChoice = 'depot';
    this.loadSubscription = this.optimizationService.getRouteEndpoints(routeId, this.data.routeDate, true).subscribe(
      response => {
        if (this.destroyed || request !== this.loadRequest || routeId !== this.data.routeId) { return; }
        this.start = response.start;
        this.end = response.end;
        this.startText = response.start ? response.start.address : '';
        this.endText = response.end ? response.end.address : '';
        this.depotAddress = response.depotAddress || '';
        this.depotLatitude = response.depotLatitude == null ? null : response.depotLatitude;
        this.depotLongitude = response.depotLongitude == null ? null : response.depotLongitude;
        this.preview = response.preview || null;
        if (!this.preview || !this.preview.options || !Array.isArray(this.preview.stops)) {
          this.loadFailed = true;
          this.error = this.translate.instant('route-endpoints.preview-unavailable');
        }
        this.endAtLastStopSupported = typeof response.endAtLastStop === 'boolean';
        this.startChoice = this.inferChoice(response.start, 'start');
        this.endChoice = response.endAtLastStop === true ? 'lastStop' : this.inferChoice(response.end, 'end');
        this.loading = false;
      },
      error => {
        if (this.destroyed || request !== this.loadRequest || routeId !== this.data.routeId) { return; }
        this.loading = false;
        this.loadFailed = true;
        this.error = this.translate.instant(error && error.status === 0
          ? 'route-endpoints.service-unavailable' : 'route-endpoints.load-error');
      }
    );
  }

  ngAfterViewInit(): void {
    this.mapsApiLoader.load().then(() => {
      if (this.destroyed) { return; }
      this.attachAutocomplete(this.startSearch, 'start');
      this.attachAutocomplete(this.endSearch, 'end');
    }).catch(() => {
      if (this.destroyed) { return; }
      this.error = this.translate.instant('route-endpoints.search-error');
    });
  }

  get usesTourPlanning(): boolean {
    return !!this.preview && this.preview.engine === 'tourPlanning';
  }

  get visibleStops() {
    return this.preview ? this.preview.stops.slice(this.stopPage * this.stopPageSize, (this.stopPage + 1) * this.stopPageSize) : [];
  }

  private text(key: string): string {
    return this.translate.instant('route-endpoints.parameters.' + key);
  }

  cancel(): void {
    this.ngOnDestroy();
    this.dialogRef.close();
  }

  coordinateText(field: 'start' | 'end'): string {
    const choice = field === 'start' ? this.startChoice : this.endChoice;
    if (choice === 'location') { return this.text('location-on-confirmation'); }
    if (choice === 'lastStop') {
      return this.translate.instant('route-endpoints.last-stop');
    }
    const endpoint = this.getEndpoint(field);
    const latitude = choice === 'depot' ? this.depotLatitude : endpoint && endpoint.latitude;
    const longitude = choice === 'depot' ? this.depotLongitude : endpoint && endpoint.longitude;
    return latitude == null || longitude == null ? this.text('coordinates-unavailable') : `${latitude}, ${longitude}`;
  }

  private attachAutocomplete(input: ElementRef<HTMLInputElement>, field: 'start' | 'end'): void {
    const autocomplete = new google.maps.places.Autocomplete(input.nativeElement, {
      types: ['address'],
      componentRestrictions: { country: 'ca' }
    });
    autocomplete.addListener('place_changed', () => this.zone.run(() => {
      const place = autocomplete.getPlace();
      if (!place.geometry || !place.geometry.location) {
        return;
      }

      const endpoint: RouteOptimizationEndpoint = {
        address: place.formatted_address || place.name || input.nativeElement.value,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng()
      };
      this.setEndpoint(field, endpoint);
      this.setText(field, endpoint.address);
      this.error = '';
    }));
  }

  private inferChoice(endpoint: RouteOptimizationEndpoint | null, field: 'start' | 'end'): RouteEndpointChoice {
    if (!endpoint) { return field === 'end' && !this.usesTourPlanning ? 'lastStop' : 'depot'; }
    const address = (endpoint.address || '').trim().toLowerCase();
    if (address === 'my saved location' || address === 'ma position enregistrée' || address === 'ma position enregistree') {
      return 'location';
    }
    if (field === 'end' && this.depotLatitude != null && this.depotLongitude != null &&
        Math.abs(endpoint.latitude - this.depotLatitude) < 0.0000001 && Math.abs(endpoint.longitude - this.depotLongitude) < 0.0000001) {
      return 'depot';
    }
    return 'custom';
  }

  onAddressEdited(field: 'start' | 'end', event: Event): void {
    // Places Autocomplete may fire an input event while replacing the text
    // with the selected formatted address. Keep the coordinates in that case.
    const input = event.target as HTMLInputElement;
    const endpoint = this.getEndpoint(field);
    if (endpoint && input.value === endpoint.address) {
      return;
    }

    this.setEndpoint(field, null);
    this.error = '';
  }

  setChoice(field: 'start' | 'end', choice: RouteEndpointChoice): void {
    if (field === 'start') { this.startChoice = choice; } else { this.endChoice = choice; }
    if (choice === 'depot' || choice === 'lastStop') {
      this.setEndpoint(field, null);
      this.setText(field, '');
    } else if (choice === 'location') {
      const endpoint = this.getEndpoint(field);
      if (!endpoint || endpoint.address !== this.translate.instant('route-endpoints.saved-location')) {
        this.setEndpoint(field, null);
        this.setText(field, this.translate.instant('route-endpoints.saved-location'));
      }
    } else if (!this.getEndpoint(field)) {
      this.setText(field, '');
    }
    this.error = '';
  }

  private getFreshLocation(): Promise<RouteOptimizationEndpoint> {
    if (!('geolocation' in navigator)) {
      return Promise.reject(new Error(this.translate.instant('route-endpoints.location-error')));
    }
    return new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(position => resolve({
      address: this.translate.instant('route-endpoints.saved-location'),
      latitude: position.coords.latitude,
      longitude: position.coords.longitude
    }), () => reject(new Error(this.translate.instant('route-endpoints.location-error'))), {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 15000
    }));
  }

  private getEndpoint(field: 'start' | 'end'): RouteOptimizationEndpoint | null {
    return field === 'start' ? this.start : this.end;
  }

  private setEndpoint(field: 'start' | 'end', endpoint: RouteOptimizationEndpoint | null): void {
    if (field === 'start') {
      this.start = endpoint;
    } else {
      this.end = endpoint;
    }
  }

  private setText(field: 'start' | 'end', text: string): void {
    if (field === 'start') {
      this.startText = text;
    } else {
      this.endText = text;
    }
  }

  save(optimize: boolean): void {
    if (this.loading || this.saving || this.loadFailed || !this.preview) {
      return;
    }
    if (this.endChoice === 'lastStop' && this.usesTourPlanning && !this.endAtLastStopSupported) {
      this.error = this.translate.instant('route-endpoints.arrival-unavailable');
      return;
    }
    const depot = this.depotEndpoint();
    if (this.endChoice === 'depot' && !depot) {
      this.error = this.translate.instant('route-endpoints.depot-coordinates-error');
      return;
    }

    if (!/^(?:[01][0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])?$/.test(this.preview.options.departureTime) ||
        !Number.isInteger(this.preview.options.stopDurationMinutes) || this.preview.options.stopDurationMinutes < 0 ||
        this.preview.options.stopDurationMinutes > 1440) {
      this.error = this.translate.instant('route-endpoints.validation-error');
      return;
    }

    if ((this.startChoice === 'custom' && !this.start) ||
        (this.endChoice === 'custom' && !this.end)) {
      this.error = this.translate.instant('route-endpoints.choose-suggestion');
      return;
    }

    this.saving = true;
    this.error = '';
    const request = this.loadRequest;
    const routeId = this.data.routeId;
    const options = Object.assign({}, this.preview.options);
    const endAtLastStop = this.endChoice === 'lastStop';
    const getEndpoint = (field: 'start' | 'end', choice: RouteEndpointChoice, endpoint: RouteOptimizationEndpoint | null) =>
      choice === 'depot' ? Promise.resolve(field === 'end' ? depot : null) : choice === 'lastStop' ? Promise.resolve(null)
        : choice === 'location' ? this.getFreshLocation() : Promise.resolve(endpoint);
    Promise.all([getEndpoint('start', this.startChoice, this.start), getEndpoint('end', this.endChoice, this.end)]).then(values => {
      if (this.destroyed || request !== this.loadRequest || routeId !== this.data.routeId) { return; }
      const endpoints: RouteOptimizationEndpoints = { start: values[0], end: values[1], endAtLastStop, options };
      this.optimizationService.saveRouteEndpoints(routeId, endpoints).subscribe(
      () => {
        if (this.destroyed || request !== this.loadRequest || routeId !== this.data.routeId) { return; }
        this.dialogRef.close(optimize);
      },
      () => {
        if (this.destroyed || request !== this.loadRequest || routeId !== this.data.routeId) { return; }
        this.saving = false;
        this.error = this.translate.instant('route-endpoints.save-error');
      }
      );
    }).catch(error => this.zone.run(() => {
      if (this.destroyed || request !== this.loadRequest || routeId !== this.data.routeId) { return; }
      this.saving = false;
      this.error = error && error.message ? error.message : this.translate.instant('route-endpoints.location-error');
    }));
  }

  private depotEndpoint(): RouteOptimizationEndpoint | null {
    if (!this.depotAddress || this.depotLatitude == null || this.depotLongitude == null ||
        !Number.isFinite(this.depotLatitude) || !Number.isFinite(this.depotLongitude) ||
        Math.abs(this.depotLatitude) > 90 || Math.abs(this.depotLongitude) > 180 ||
        (this.depotLatitude === 0 && this.depotLongitude === 0)) { return null; }
    return { address: this.depotAddress, latitude: this.depotLatitude, longitude: this.depotLongitude };
  }
}
